/**
 * Modal dialog on the native <dialog> element: real focus containment, Escape to close, backdrop, and
 * focus returned to the opener. Title is a question naming the object; the affirmative sits right (§11).
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export function Dialog({ open, onClose, title, description, children, footer, size = 'md', closeDisabled = false }: {
  open: boolean; onClose: () => void; title: string; description?: ReactNode; children?: ReactNode; footer?: ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl';
  /** The close button is off while something in the dialog must finish first. */
  closeDisabled?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId(); // one per dialog: a shared id would let every dialog borrow the open one's name
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement as HTMLElement;
      d.showModal();
      // showModal focuses the first button (Close); a form should start in its first field
      const first = d.querySelector<HTMLElement>('[data-autofocus], input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([disabled]), select:not([disabled]), textarea:not([disabled])');
      first?.focus();
    } else if (!open && d.open) {
      d.close();
      opener.current?.focus?.();
    }
  }, [open]);
  return (
    <dialog
      ref={ref}
      // React hands a nested <dialog>'s cancel/close to every ancestor dialog too — act only on our own
      onCancel={(e) => { if (e.target !== ref.current) return; e.preventDefault(); onClose(); }}
      onClose={(e) => { if (e.target === ref.current && open) onClose(); }}
      aria-labelledby={titleId}
      className={cn('z-modal m-auto w-[calc(100vw-2rem)] rounded-panel bg-popover p-0 text-ink elevate backdrop:bg-scrim', size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : size === 'xl' ? 'max-w-4xl' : 'max-w-lg')}
    >
      {open && (
        <div className="flex max-h-[85dvh] flex-col">
          <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
            <div>
              <h2 id={titleId} className="text-[16px] font-semibold">{title}</h2>
              {description && <div className="mt-1 text-sm text-ink-2">{description}</div>}
            </div>
            <button type="button" className="inline-flex size-8 shrink-0 items-center justify-center rounded-control text-muted hover:bg-surface-2 hover:text-ink disabled:pointer-events-none disabled:opacity-50" aria-label="Close" onClick={onClose} disabled={closeDisabled}><X className="size-4" aria-hidden /></button>
          </div>
          <div className="min-h-0 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}
